import { useEffect, useState } from 'react'
import { teacherPriorityApi } from '@/services/api/teacherPriority'

interface Recipient {
  user_id: string
  name: string
}

interface NoTeacherFallbackProps {
  requestId: string
  lessonLabel?: string
  onClose?: () => void
  onForwarded?: () => void
}

/**
 * Konzept A — Fallback-Panel für den Fall, dass (für gewählte Lektionen) kein
 * Vertretungslehrer verfügbar ist. Die Lehrkraft kann die Anfrage an einen,
 * mehrere oder alle Admins weiterleiten (Auto-Login-Email + App-Badge) oder
 * einen Admin direkt per Chat kontaktieren.
 */
export function NoTeacherFallback({ requestId, lessonLabel, onClose, onForwarded }: NoTeacherFallbackProps) {
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    teacherPriorityApi
      .getNoSubstituteRecipients()
      .then(res => {
        if (!alive) return
        const list: Recipient[] = res?.recipients ?? []
        setRecipients(list)
        // default: alle vorauswählen
        setSelected(new Set(list.map(r => r.user_id)))
      })
      .catch(() => alive && setError('Empfänger konnten nicht geladen werden.'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === recipients.length) setSelected(new Set())
    else setSelected(new Set(recipients.map(r => r.user_id)))
  }

  const handleForward = async () => {
    if (selected.size === 0) {
      setError('Bitte mindestens einen Empfänger auswählen.')
      return
    }
    setSending(true)
    setError(null)
    try {
      await teacherPriorityApi.forwardToAdmin(requestId, {
        recipient_user_ids: Array.from(selected),
        message: message.trim() || undefined,
      })
      setDone(true)
      onForwarded?.()
    } catch (e) {
      const err = e as { body?: { detail?: string }; message?: string }
      setError(err?.body?.detail || err?.message || 'Weiterleitung fehlgeschlagen.')
    } finally {
      setSending(false)
    }
  }

  if (done) {
    return (
      <div className="p-5 bg-white rounded-lg shadow border border-slate-200 text-center">
        <p className="text-sm font-medium text-emerald-700">Anfrage an Admin weitergeleitet.</p>
        <p className="text-xs text-slate-500 mt-1">Die Admins wurden per Email (mit Login-Link) und in der App benachrichtigt.</p>
        {onClose && (
          <button onClick={onClose} className="mt-3 px-3 py-1.5 text-sm rounded-lg bg-slate-100 hover:bg-slate-200">
            Schliessen
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="p-5 bg-white rounded-lg shadow border-2 border-amber-300">
      <h3 className="text-base font-semibold text-slate-800">Kein Vertretungslehrer verfügbar</h3>
      <p className="text-sm text-slate-600 mt-1">
        {lessonLabel ? `Für „${lessonLabel}" ` : 'Für diese Lektionen '}ist aktuell kein Lehrer verfügbar.
        Du kannst die Anfrage an Admins weiterleiten oder einen Admin direkt kontaktieren.
      </p>

      {loading ? (
        <p className="text-sm text-slate-400 mt-3">Empfänger werden geladen …</p>
      ) : recipients.length === 0 ? (
        <p className="text-sm text-red-600 mt-3">Kein Admin/Empfänger hinterlegt. Bitte wende dich an die Schulleitung.</p>
      ) : (
        <>
          <div className="flex items-center justify-between mt-4 mb-1">
            <span className="text-xs font-medium text-slate-500">An Admin weiterleiten an:</span>
            <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline">
              {selected.size === recipients.length ? 'Alle abwählen' : 'Alle auswählen'}
            </button>
          </div>
          <div className="space-y-1 max-h-44 overflow-y-auto">
            {recipients.map(r => (
              <label key={r.user_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-blue-600"
                  checked={selected.has(r.user_id)}
                  onChange={() => toggle(r.user_id)}
                />
                <span className="text-sm text-slate-700">{r.name}</span>
              </label>
            ))}
          </div>

          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Nachricht an den Admin (optional) …"
            className="w-full mt-3 px-3 py-2 text-sm border border-slate-300 rounded-lg resize-none"
            rows={2}
          />
        </>
      )}

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      <div className="flex gap-2 mt-4">
        <a
          href="/teacher/chats"
          className="px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
        >
          Admin kontaktieren (Chat)
        </a>
        <button
          onClick={handleForward}
          disabled={sending || selected.size === 0}
          className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? 'Wird weitergeleitet …' : `An ${selected.size} Admin(s) weiterleiten`}
        </button>
      </div>
    </div>
  )
}
